import type { ReactNode } from 'react';
import type {
  ChatUiFlightConnection,
  ChatUiFlightSegment,
} from '@voll-intelligence/genui-contract';
import { requireValue } from '../require_value.js';
import { fmtTime } from './flight_format.js';
import {
  FlightDetailDrawerShell,
  FlightDetailView,
  type FlightTimelineLeg,
} from '@voll-intelligence/intelligence-ui-kit';
import {
  enrichmentSoldAsLabel,
  enrichmentStatsLabel,
  hasEnrichmentData,
  useSegmentEnrichment,
  withTerminal,
  type FlightSegmentEnrichment,
} from './flight_enrichment.js';
import { useGenUiI18n } from './i18n.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import './flight_ui.css';

/**
 * Primitivas COMPARTILHADAS dos cards de voo ricos (FlightReservationCard + o
 * painel de detalhe do voo do checkout unificado): a pilha de trechos e o
 * drawer informativo do trecho. Sem estado de domínio — só projeção
 * display-only do `ChatUiFlightSegment`.
 *
 * A LINHA do trecho não mora mais aqui: ela é a `FlightLeg` (`shared/ui/
 * flight_leg`), a mesma que a linha de ofertas desenha. Estes cards a pedem com
 * os dois opt-ins de recibo (`showCarrier` + "Detalhes" por perna). Estilo dos
 * trechos no `flight_ui.css` (`.fq-*`).
 *
 * O breakdown Tarifa+Taxas=Total saiu daqui: chamava-se compartilhado, mas tinha
 * o `FlightReservationCard` como único consumidor, e hoje mora nele.
 */

/** Pilha vertical de trechos — o contêiner que os cards de recibo usam no lugar
 *  do grid de duas colunas da linha de ofertas. N trechos (multi-trecho da
 *  reserva) empilham com a mesma régua; a divisória entre eles vem do CSS
 *  (`.fq-legs--stack .fq-leg + .fq-leg`), não de um nó irmão. */
export function FlightLegStack({ children }: { readonly children: ReactNode }) {
  return <div className="fq-legs fq-legs--stack">{children}</div>;
}

/** A perna única do drawer do card: horário/IATA do wire, decorada com o
 *  enriquecimento Cirium quando presente (aeronave na linha de duração,
 *  pontualidade, codeshares, terminal como "nome" do nó). Sem enriquecimento,
 *  reproduz exatamente a perna seca de sempre. */
function toSegmentTimelineLeg(
  seg: ChatUiFlightSegment,
  enrichment: FlightSegmentEnrichment | null,
  t: ReturnType<typeof useGenUiI18n>['t'],
): FlightTimelineLeg {
  const durationLabel = [seg.durationLabel, enrichment?.aircraftName].filter(Boolean).join(' · ');
  const statsLabel = enrichmentStatsLabel(
    enrichment?.onTimePercent,
    enrichment?.onTimeObservations,
    t,
  );
  const soldAsLabel = enrichmentSoldAsLabel(enrichment?.soldAs, t);
  const originName = withTerminal(undefined, enrichment?.departureTerminal, t);
  const destinationName = withTerminal(undefined, enrichment?.arrivalTerminal, t);
  return {
    origin: {
      time: fmtTime(seg.departAt),
      iata: seg.from,
      ...(originName ? { name: originName } : {}),
    },
    destination: {
      time: fmtTime(seg.arriveAt),
      iata: seg.to,
      ...(destinationName ? { name: destinationName } : {}),
    },
    ...(durationLabel ? { durationLabel } : {}),
    ...(statsLabel ? { statsLabel } : {}),
    ...(soldAsLabel ? { soldAsLabel } : {}),
  };
}

/** As pernas do drawer de um trecho COM escalas. O wire só traz o IATA e o
 *  layover de cada escala, então N escalas viram N+1 pernas cujos nós
 *  intermediários ficam sem horário (nunca inventado) e cuja caixa de parada só
 *  existe quando o layover veio. A duração do wire vai de ponta a ponta: entra
 *  UMA vez, na primeira perna, dita como total — repeti-la por perna, ou
 *  deixá-la crua sob a partida, a faria passar pela duração do primeiro voo. */
function toConnectingTimelineLegs(
  seg: ChatUiFlightSegment,
  connections: readonly ChatUiFlightConnection[],
  t: ReturnType<typeof useGenUiI18n>['t'],
): FlightTimelineLeg[] {
  const points = [
    { time: fmtTime(seg.departAt), iata: seg.from },
    ...connections.map((c) => ({ time: '', iata: c.iata })),
    { time: fmtTime(seg.arriveAt), iata: seg.to },
  ];
  return points.slice(1).map((destination, i) => {
    const stop = connections[i];
    const stopLabel = stop?.layoverLabel
      ? t('flightDetail.connection', { duration: stop.layoverLabel, airport: stop.iata })
      : undefined;
    return {
      origin: requireValue(points[i]),
      destination,
      ...(i === 0 && seg.durationLabel
        ? { durationLabel: t('flightDetail.totalDuration', { duration: seg.durationLabel }) }
        : {}),
      ...(stopLabel ? { stopLabel } : {}),
    };
  });
}

/** Drawer informativo do trecho ("Detalhes"). Monta o shell compartilhado
 *  `FlightDetailDrawerShell` (o `Sheet` do DS, modal) e renderiza a
 *  vista `FlightDetailView` (cabeçalho da cia + linha do tempo) — mesmo shell do
 *  drawer "Detalhe do voo" das ofertas. Os cards só têm o segmento exibido: voo
 *  direto é UMA perna; com escalas, uma perna por voo a partir de
 *  `seg.connections` (parada + espera entre elas). A rota e a perna
 *  ("Ida"/"Volta") migram para o cabeçalho da cia. Sem rodapé — o voo já foi
 *  escolhido nesses cards (o "Selecionar voo" é exclusivo do drawer de ofertas). */
export function SegmentDetailDrawer({
  seg,
  dir,
  onClose,
}: {
  readonly seg: ChatUiFlightSegment;
  readonly dir: string;
  readonly onClose: () => void;
}) {
  const { t } = useGenUiI18n();
  const flightNo = [seg.carrier, seg.flightNumber].filter(Boolean).join(' ');
  // Enriquecimento Cirium lazy por identidade do voo (só trecho direto, port
  // opcional) — decora a perna única; sem port/dado, o drawer fica como sempre.
  const enrichment = useSegmentEnrichment(seg);
  const connections = seg.connections ?? [];
  const legs =
    connections.length > 0
      ? toConnectingTimelineLegs(seg, connections, t)
      : [toSegmentTimelineLeg(seg, enrichment, t)];

  return (
    <FlightDetailDrawerShell
      title={t('flight.segment.detailsTitle')}
      ariaLabel={t('flight.segment.detailsTitle')}
      backLabel={t('flight.segment.detailBack')}
      onClose={onClose}
    >
      <FlightDetailView
        header={{
          ...(seg.carrier ? { carrier: seg.carrier } : {}),
          ...(seg.carrierName ? { carrierName: seg.carrierName } : {}),
          ...(flightNo ? { flightNo } : {}),
          route: `${seg.from} → ${seg.to}`,
          ...(dir ? { dirLabel: dir } : {}),
        }}
        legs={legs}
      />
      {hasEnrichmentData(enrichment) && (
        <p className="fdt-partner-note">{t('flightDetail.partnerDataNote')}</p>
      )}
    </FlightDetailDrawerShell>
  );
}
